import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Divider, IconButton, Stack, Typography } from "@mui/material";
import { CartItem, EventDetails } from "../types";
import { MobileBottomCart, PremiumButton } from "../styles";
import { rupiah } from "../../../../utils/currency";

interface MobileBottomCartProps {
  filteredItems: CartItem[];
  totalTickets: number;
  totalFinalPrice: number;
  totalPrice: number;
  eventDetails: EventDetails;
  onProceed: () => void;
  showPriceBreakdown: boolean;
  setShowPriceBreakdown: (show: boolean) => void;
}

interface CartSummaryHeaderProps {
  totalTickets: number;
  totalFinalPrice: number;
  showPriceBreakdown: boolean;
  setShowPriceBreakdown: (show: boolean) => void;
  onProceed: () => void;
}

interface PriceBreakdownDetailsProps {
  filteredItems: CartItem[];
  totalPrice: number;
  totalFinalPrice: number;
  eventDetails: EventDetails;
}

interface OrderItemsListProps {
  items: CartItem[];
}

interface OrderItemRowProps {
  item: CartItem;
}

interface PriceCalculationBreakdownProps {
  totalPrice: number;
  totalFinalPrice: number;
  eventDetails: EventDetails;
}

interface PriceBreakdownRowProps {
  label: string;
  amount: number;
}

function CartSummaryHeader({
  totalTickets,
  totalFinalPrice,
  showPriceBreakdown,
  setShowPriceBreakdown,
  onProceed
}: CartSummaryHeaderProps) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: showPriceBreakdown ? 2 : 0 }}>
      <Box>
        <Typography variant="body2" color="textSecondary">
          {totalTickets} tickets
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="h6" fontWeight="bold" color="primary">
            {rupiah(totalFinalPrice)}
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
            sx={{ 
              color: 'primary.main',
              '&:hover': { backgroundColor: 'primary.light', color: 'white' }
            }}
          >
            {showPriceBreakdown ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>
      <PremiumButton
        variant="contained"
        onClick={onProceed}
        sx={{ px: 4, py: 1.5 }}
      >
        Checkout
      </PremiumButton>
    </Box>
  );
}

function OrderItemRow({ item }: OrderItemRowProps) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Box flex={1}>
        <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.875rem' }}>
          {item.category_name}
        </Typography>
        <Typography variant="caption" color="textSecondary">
          {rupiah(item.price)} × {item.quantity}
        </Typography>
      </Box>
      <Typography variant="body2" fontWeight="bold">
        {rupiah(item.price * item.quantity)}
      </Typography>
    </Box>
  );
}

function OrderItemsList({ items }: OrderItemsListProps) {
  return (
    <>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
        Order Details
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 2 }}>
        {items.map((item) => (
          <OrderItemRow key={item.category_id} item={item} />
        ))}
      </Stack>
    </>
  );
}

function PriceBreakdownRow({ label, amount }: PriceBreakdownRowProps) {
  return (
    <Box display="flex" justifyContent="space-between">
      <Typography variant="body2" color="textSecondary">
        {label}
      </Typography>
      <Typography variant="body2">
        {rupiah(amount)}
      </Typography>
    </Box>
  );
}

function PriceCalculationBreakdown({
  totalPrice,
  totalFinalPrice,
  eventDetails
}: PriceCalculationBreakdownProps) {
  const taxAmount = eventDetails.include_tax ? 0 : (totalPrice * eventDetails.ppn) / 100;
  const platformFeeAmount = eventDetails.is_persen
    ? (totalPrice * eventDetails.fee) / 100
    : eventDetails.fee;

  return (
    <>
      <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1.5 }}>
        Price Breakdown
      </Typography>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <PriceBreakdownRow
          label="Subtotal"
          amount={totalPrice}
        />
        
        {!eventDetails.include_tax && (
          <PriceBreakdownRow
            label={`Tax (${eventDetails.ppn}%)`}
            amount={taxAmount}
          />
        )}
        
        <PriceBreakdownRow
          label={`Platform Fee ${eventDetails.is_persen ? `(${eventDetails.fee}%)` : ''}`}
          amount={platformFeeAmount}
        />
      </Stack>
      
      <Divider sx={{ mb: 2 }} />
      
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="h6" fontWeight="bold">Total</Typography>
        <Typography variant="h6" fontWeight="bold" color="primary">
          {rupiah(totalFinalPrice)}
        </Typography>
      </Box>
    </>
  );
}

function PriceBreakdownDetails({
  filteredItems,
  totalPrice,
  totalFinalPrice,
  eventDetails
}: PriceBreakdownDetailsProps) {
  return (
    <Box>
      <Divider sx={{ mb: 2 }} />
      
      <OrderItemsList items={filteredItems} />
      
      <Divider sx={{ mb: 2 }} />

      <PriceCalculationBreakdown
        totalPrice={totalPrice}
        totalFinalPrice={totalFinalPrice}
        eventDetails={eventDetails}
      />
    </Box>
  );
}

export function MobileBottomCartComponent({
  filteredItems,
  totalTickets,
  totalFinalPrice,
  totalPrice,
  eventDetails,
  onProceed,
  showPriceBreakdown,
  setShowPriceBreakdown
}: MobileBottomCartProps) {
  return (
    <MobileBottomCart>
      <Box sx={{ p: 2 }}>
        <CartSummaryHeader
          totalTickets={totalTickets}
          totalFinalPrice={totalFinalPrice}
          showPriceBreakdown={showPriceBreakdown}
          setShowPriceBreakdown={setShowPriceBreakdown}
          onProceed={onProceed}
        />

        {showPriceBreakdown && (
          <PriceBreakdownDetails
            filteredItems={filteredItems}
            totalPrice={totalPrice}
            totalFinalPrice={totalFinalPrice}
            eventDetails={eventDetails}
          />
        )}
      </Box>
    </MobileBottomCart>
  );
}
